import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { images } from "@/i18n/translations";
import { MaskLines } from "@/components/site/Reveal";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-ink"
    >
      {/* Parallax image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 h-full w-full will-change-transform"
      >
        <img
          src={images.hero}
          alt="Hunter and dog at dawn on the plain"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Scrim */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/25 to-ink/40"
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-5 pb-28 md:px-10 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          data-testid="hero-overline"
          className="mb-5 font-mono text-[11px] uppercase tracking-[0.3em] text-bone/80"
        >
          {t.hero.overline}
        </motion.p>

        <h1
          data-testid="hero-title"
          aria-label={t.hero.titleLines.join(" ")}
          className="font-display text-[19vw] font-medium uppercase leading-[0.82] tracking-[-0.02em] text-bone md:text-[13vw] lg:text-[11rem]"
        >
          <MaskLines lines={t.hero.titleLines} start={0.35} aria-hidden="true" />
        </h1>

        <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            data-testid="hero-script"
            className="font-script text-3xl text-rust md:text-4xl"
          >
            {t.hero.script}
          </motion.p>

          <motion.a
            href="#tarife"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.7 }}
            className="group inline-flex w-fit items-center gap-3 rounded-full border border-bone/40 px-7 py-3.5 font-body text-[13px] font-medium uppercase tracking-[0.16em] text-bone backdrop-blur-sm transition-colors duration-500 hover:bg-bone hover:text-ink"
          >
            {t.hero.cta}
            <span className="transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </motion.a>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/70">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ height: [8, 22, 8], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px bg-bone/70"
        />
      </motion.div>
    </section>
  );
};
